import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";

// Types from the backend csv-upload service
export interface ParsedPiece {
  count: number;
  type: string;
  mold: string;
  location: string;
  mark: string;
  volume: string;
  weight: string;
  bidNumber: string;
  pourNumber: string;
  uniquePieceIdentifier: string;
  shippingDate: string;
  productCode: string;
  priority: number | null;

  // New template fields (2026-04 upload template) — all optional
  startDate?: string;
  level?: string;
  sqFt?: string;
  linearFt?: string;
  releaseStrength?: string;
  shippingStrength?: string;
  pmDraftingNotes?: string;
  mixDesign?: string;
  color?: string;
  finish?: string;
  moldProductionHours?: string;
  setupHours?: string;
  castingHours?: string;
  finishingHours?: string;
}

export interface MoldGroup {
  moldNumber: string;
  pieceCount: number;
  suggestedMoldCount: number;
  pieces: ParsedPiece[];
  duration: string | null;
  // Per-mold phase durations derived from the first piece's per-day columns.
  // Null means the sheet didn't supply them and defaults should be used.
  setupHours?: string | null;
  castingHours?: string | null;
  finishingHours?: string | null;
}

export interface ValidationError {
  row: number;
  field: string;
  message: string;
}

export interface PreviewResponse {
  pieces: ParsedPiece[];
  moldGroups: MoldGroup[];
  errors: ValidationError[];
}

// Editable mold group config used in the UI
export interface MoldGroupConfig {
  moldNumber: string;
  moldCount: number;
  moldProductionHours: string;    // e.g., "8,8,2" - per-mold production hours
  setupHours?: string;             // Per-mold from sheet; falls back to view-store global if blank
  castingHours?: string;
  finishingHours?: string;
  pieceCount: number;
  pieces: ParsedPiece[];
  productionArea: string;  // Production area/form assignment
  priority: number | null; // Scheduling priority (1 = highest priority)
  linearFt: string | null; // Optional linear-feet measurement, applied to all pieces in this group
  scheduleMode?: 'shipDate' | 'startDate'; // 'shipDate' = backward (default), 'startDate' = forward
  startDate?: string; // Used when scheduleMode === 'startDate' (yyyy-MM-dd)
  setupToCastingGap?: number; // Business days between setup end and casting start (0, 1, or 2). Default 0.
}

// Legacy types kept for backward compatibility with Dialog component
export interface ParsedRow {
  count: number;
  type: string;
  mold: string;
  location: string;
  mark: string;
  volume: string;
  weight: string;
  volumeCuft: number;
  weightLbs: number;
  bidNumber: string;
  pourNumber: string;
  uniquePieceIdentifier: string;
  numberOfMolds: number | null;
  duration: string;
  areaForm: string;
  productCode: string;
  markNumber: string;
}

// ===== Simulation Types =====

export interface ScheduleChip {
  panelId: number;
  panelIdentifier: string;
  date: string;
  phase: 'moldProduction' | 'setup' | 'casting' | 'finishing' | 'shipping';
  dayNumber: number;
  hours: number;
  moldInstanceCode: string;
}

export interface ScheduleConflict {
  type: 'deadline_missed';
  moldNumber: string;
  moldInstanceCode: string;
  panelIdentifier: string;
  originalShippingDate: string;
  calculatedShippingDate: string;
  daysBehind: number;
  suggestion: string;
}

export interface ScheduleWarning {
  type: 'tight_schedule' | 'weekend_skip' | 'no_buffer';
  message: string;
  affectedPanels: string[];
}

export interface DepartmentCapacityInfo {
  available: number;
  allocated: number;
  remaining: number;
  utilizationPercent: number;
  isOverCapacity: boolean;
}

export interface DayCapacity {
  date: string;
  availableManHours: number;
  allocatedManHours: number;
  remainingManHours: number;
  utilizationPercent: number;
  isHoliday: boolean;
  isWeekend: boolean;
  notes?: string | null;
  departmentCapacity?: {
    moldProduction: DepartmentCapacityInfo;
    setup: DepartmentCapacityInfo;
    casting: DepartmentCapacityInfo;
    finishing: DepartmentCapacityInfo;
  };
}

export interface ScheduleSimulation {
  chips: ScheduleChip[];
  conflicts: ScheduleConflict[];
  warnings: ScheduleWarning[];
  dateRange: {
    startDate: string;
    endDate: string;
  };
  capacityByDate: DayCapacity[];
}

// Individual chip position (for manual adjustments after initial placement)
export interface ChipPosition {
  phase: 'moldProduction' | 'setup' | 'casting' | 'finishing' | 'shipping';
  dayNumber: number;
  date: string; // YYYY-MM-DD
}

// Manual placement for a piece
export interface ManualPlacement {
  pieceIndex: number; // Index in pieces array (unique within session)
  pieceIdentifier: string; // For display/backend
  moldInstanceCode: string;
  startDate: string; // Mold production start date (YYYY-MM-DD) - used for initial calculation
  chipPositions?: ChipPosition[]; // Override positions for individual chips (after manual adjustment)
}

export type SchedulingMode = 'manual' | 'auto';

/**
 * ProductionScheduleUploadViewStore
 *
 * Uses the backend csv-upload service for preview and confirm operations.
 * Implements the new phase-based scheduling flow.
 */
export class ProductionScheduleUploadViewStore {
  rootStore: RootStore;

  // File state
  fileName: string | null = null;
  rawCsvData: string = "";

  // Preview response from backend
  pieces: ParsedPiece[] = [];
  moldGroupConfigs: MoldGroupConfig[] = [];
  /**
   * True when the uploaded sheet carried no recognizable start date, so every
   * mould group defaulted to scheduling from today rather than from the file.
   */
  noStartDateColumn = false;
  validationErrors: ValidationError[] = [];

  // Form state
  projectId: string = "";
  bidNumber: string = "";

  // UI state
  uploading: boolean = false;
  saving: boolean = false;
  uploadProgress: number = 0;
  error: string | null = null;
  success: string | null = null;

  // Wizard state
  activeStep: number = 0;

  // Global phase durations (applied to all molds)
  setupHours: string = '2';         // e.g., "2" or "4,4"
  castingHours: string = '1';       // e.g., "1" (60 min) - stored as hours, displayed as minutes
  finishingHours: string = '2,2';   // e.g., "2,2"

  // Simulation state
  simulationData: ScheduleSimulation | null = null;
  simulationLoading: boolean = false;

  // Manual scheduling state
  schedulingMode: SchedulingMode = 'manual';
  manualPlacements: Map<number, ManualPlacement> = new Map(); // keyed by pieceIndex
  draggedPieceIndex: number | null = null;
  existingCapacity: DayCapacity[] = []; // Capacity from existing scheduled items in DB

  // Pending mold configs from imported config CSV (applied after pieces are parsed)
  private pendingMoldConfigs: Map<string, { moldCount: number; moldProductionHours: string; productionArea: string; shipDate: string; priority: number | null }> = new Map();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ===== Computed Properties =====

  get hasData(): boolean {
    return this.moldGroupConfigs.length > 0;
  }

  get canSave(): boolean {
    if (!this.projectId) return false;
    if (this.moldGroupConfigs.length === 0) return false;
    if (this.moldGroupConfigs.some((g) => !g.moldProductionHours?.trim())) return false;
    return true;
  }

  get missingFields(): string[] {
    const missing: string[] = [];
    if (!this.projectId) missing.push("Select a project");
    if (this.moldGroupConfigs.some((g) => !g.moldProductionHours?.trim())) {
      missing.push("Set Mold Production Hours for all molds");
    }
    return missing;
  }

  // Legacy computed properties for backward compatibility with Dialog
  get moldGroups(): any[] {
    return this.moldGroupConfigs.map((config) => ({
      type: config.moldNumber,
      mold: config.moldNumber,
      moldNumber: config.moldNumber,
      pieceCount: config.pieceCount,
      marks: config.pieces.map(p => p.mark),
      productionHoursPerDay: config.moldProductionHours,
      numberOfMolds: config.moldCount,
      areaForm: config.productionArea || '',
      productionArea: config.productionArea || '',
      bidNumber: this.bidNumber,
      panels: config.pieces,
      productCode: config.pieces[0]?.productCode || '',
      markNumber: config.pieces[0]?.type || '',
      dueDate: config.pieces[0]?.shippingDate || null,
      // New fields
      suggestedMoldCount: config.moldCount,
      duration: config.moldProductionHours,
    }));
  }

  get parsedRows(): ParsedRow[] {
    return this.pieces.map((p) => ({
      count: p.count,
      type: p.type,
      mold: p.mold,
      location: p.location,
      mark: p.mark,
      volume: p.volume,
      weight: p.weight,
      volumeCuft: parseFloat(p.volume) || 0,
      weightLbs: parseFloat(p.weight) || 0,
      bidNumber: p.bidNumber,
      pourNumber: p.pourNumber,
      uniquePieceIdentifier: p.uniquePieceIdentifier,
      numberOfMolds: null,
      duration: '',
      areaForm: '',
      productCode: p.productCode,
      markNumber: p.type,
    }));
  }

  get hasManpowerCapacity(): boolean {
    const manhourCapacityStore = (this.rootStore as any).manhourCapacityStore;
    const capacities = manhourCapacityStore?.entities || [];
    return capacities.length > 0;
  }

  // ===== Simulation Computed Properties =====

  get hasConflicts(): boolean {
    return (this.simulationData?.conflicts?.length || 0) > 0;
  }

  // System conflicts are capacity-related bugs that should block import
  // Regular deadline conflicts (late panels) should NOT block import
  get hasSystemConflicts(): boolean {
    return (this.simulationData?.conflicts || []).some(
      c => c.moldNumber === 'SYSTEM'
    );
  }

  // Regular deadline conflicts - panels that will ship late but import is allowed
  get hasLateConflicts(): boolean {
    return (this.simulationData?.conflicts || []).some(
      c => c.moldNumber !== 'SYSTEM' && c.type === 'deadline_missed'
    );
  }

  get canProceedToConfirm(): boolean {
    return !this.hasSystemConflicts && this.canSave;
  }

  get conflictCount(): number {
    return this.simulationData?.conflicts?.length || 0;
  }

  // ===== Actions =====

  setProjectId = (projectId: string) => {
    this.projectId = projectId;
  };

  setBidNumber = (bidNumber: string) => {
    this.bidNumber = bidNumber;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  setUploading = (uploading: boolean) => {
    this.uploading = uploading;
  };

  setSaving = (saving: boolean) => {
    this.saving = saving;
  };

  setUploadProgress = (progress: number) => {
    this.uploadProgress = progress;
  };

  // ===== Mold Group Config Updates =====

  updateMoldGroupMoldCount = (index: number, value: number) => {
    if (index >= 0 && index < this.moldGroupConfigs.length) {
      this.moldGroupConfigs[index] = {
        ...this.moldGroupConfigs[index],
        moldCount: Math.max(1, value)
      };
    }
  };

  updateMoldGroupMoldProductionHours = (index: number, value: string) => {
    if (index >= 0 && index < this.moldGroupConfigs.length) {
      this.moldGroupConfigs[index] = {
        ...this.moldGroupConfigs[index],
        moldProductionHours: value
      };
    }
  };

  // ===== Global Setup Hours Chip Helpers =====

  addSetupHourChip = () => {
    const currentHours = this.parseHoursToArray(this.setupHours);
    currentHours.push(2); // Default 2 hours for new setup day
    this.setupHours = this.arrayToHoursString(currentHours);
    this.clearSimulation();
  };

  changeSetupHourChip = (chipIndex: number, newValue: number) => {
    const currentHours = this.parseHoursToArray(this.setupHours);
    if (chipIndex >= 0 && chipIndex < currentHours.length) {
      currentHours[chipIndex] = newValue;
      this.setupHours = this.arrayToHoursString(currentHours);
      this.clearSimulation();
    }
  };

  removeSetupHourChip = (chipIndex: number) => {
    const currentHours = this.parseHoursToArray(this.setupHours);
    if (chipIndex >= 0 && chipIndex < currentHours.length && currentHours.length > 1) {
      currentHours.splice(chipIndex, 1);
      this.setupHours = this.arrayToHoursString(currentHours);
      this.clearSimulation();
    }
  };

  // ===== Global Finishing Hours Chip Helpers =====

  addFinishingHourChip = () => {
    const currentHours = this.parseHoursToArray(this.finishingHours);
    currentHours.push(2); // Default 2 hours for new finishing day
    this.finishingHours = this.arrayToHoursString(currentHours);
    this.clearSimulation();
  };

  changeFinishingHourChip = (chipIndex: number, newValue: number) => {
    const currentHours = this.parseHoursToArray(this.finishingHours);
    if (chipIndex >= 0 && chipIndex < currentHours.length) {
      currentHours[chipIndex] = newValue;
      this.finishingHours = this.arrayToHoursString(currentHours);
      this.clearSimulation();
    }
  };

  removeFinishingHourChip = (chipIndex: number) => {
    const currentHours = this.parseHoursToArray(this.finishingHours);
    if (chipIndex >= 0 && chipIndex < currentHours.length && currentHours.length > 1) {
      currentHours.splice(chipIndex, 1);
      this.finishingHours = this.arrayToHoursString(currentHours);
      this.clearSimulation();
    }
  };

  // ===== Global Casting Hours Chip Helpers =====

  addCastingHourChip = () => {
    const currentHours = this.parseHoursToArray(this.castingHours);
    currentHours.push(1); // Default 1 hour (60 minutes) for new casting day
    this.castingHours = this.arrayToHoursString(currentHours);
    this.clearSimulation();
  };

  changeCastingHourChip = (chipIndex: number, newValue: number) => {
    const currentHours = this.parseHoursToArray(this.castingHours);
    if (chipIndex >= 0 && chipIndex < currentHours.length) {
      currentHours[chipIndex] = newValue;
      this.castingHours = this.arrayToHoursString(currentHours);
      this.clearSimulation();
    }
  };

  removeCastingHourChip = (chipIndex: number) => {
    const currentHours = this.parseHoursToArray(this.castingHours);
    if (chipIndex >= 0 && chipIndex < currentHours.length && currentHours.length > 1) {
      currentHours.splice(chipIndex, 1);
      this.castingHours = this.arrayToHoursString(currentHours);
      this.clearSimulation();
    }
  };

  // Legacy method aliases for backward compatibility
  updateMoldGroupHours = (index: number, value: string) => {
    this.updateMoldGroupMoldProductionHours(index, value);
  };

  updateMoldGroupNumberOfMolds = (index: number, value: number) => {
    this.updateMoldGroupMoldCount(index, value);
  };

  updateMoldGroupDueDate = (index: number, value: string | null) => {
    // Due dates are now on individual pieces via shippingDate
    // This is kept for backward compatibility but doesn't do anything meaningful
    console.log('updateMoldGroupDueDate is deprecated - shipping dates are set per piece');
  };

  updateMoldGroupAreaForm = (index: number, value: string) => {
    if (index >= 0 && index < this.moldGroupConfigs.length) {
      this.moldGroupConfigs[index] = {
        ...this.moldGroupConfigs[index],
        productionArea: value
      };
    }
  };

  /**
   * Update the shipping date for all pieces in a mold group
   */
  updateMoldGroupShippingDate = (index: number, date: Date | null) => {
    if (index >= 0 && index < this.moldGroupConfigs.length && date) {
      const dateStr = date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
      const updatedPieces = this.moldGroupConfigs[index].pieces.map(piece => ({
        ...piece,
        shippingDate: dateStr
      }));

      this.moldGroupConfigs[index] = {
        ...this.moldGroupConfigs[index],
        pieces: updatedPieces
      };

      // Also update the main pieces array to keep them in sync
      for (const piece of updatedPieces) {
        const mainPieceIndex = this.pieces.findIndex(
          p => p.uniquePieceIdentifier === piece.uniquePieceIdentifier
        );
        if (mainPieceIndex >= 0) {
          this.pieces[mainPieceIndex] = {
            ...this.pieces[mainPieceIndex],
            shippingDate: dateStr
          };
        }
      }
    }
  };

  /**
   * Update the schedule mode for a mold group (shipDate = backward, startDate = forward)
   */
  updateMoldGroupScheduleMode = (index: number, mode: 'shipDate' | 'startDate') => {
    if (index >= 0 && index < this.moldGroupConfigs.length) {
      this.moldGroupConfigs[index] = {
        ...this.moldGroupConfigs[index],
        scheduleMode: mode,
      };
      this.clearSimulation();
    }
  };

  /**
   * Update the setup→casting gap for a mold group (0, 1, or 2 business days)
   */
  updateMoldGroupSetupToCastingGap = (index: number, gap: number) => {
    if (index >= 0 && index < this.moldGroupConfigs.length) {
      this.moldGroupConfigs[index] = {
        ...this.moldGroupConfigs[index],
        setupToCastingGap: gap,
      };
      this.clearSimulation();
    }
  };

  /**
   * Update the explicit start date for a mold group (used when scheduleMode === 'startDate')
   */
  updateMoldGroupStartDate = (index: number, date: Date | null) => {
    if (index >= 0 && index < this.moldGroupConfigs.length) {
      const dateStr = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : undefined;
      this.moldGroupConfigs[index] = {
        ...this.moldGroupConfigs[index],
        startDate: dateStr,
      };
      this.clearSimulation();
    }
  };

  /**
   * Update the priority for all pieces in a mold group
   */
  updateMoldGroupPriority = (index: number, priority: number | null) => {
    if (index >= 0 && index < this.moldGroupConfigs.length) {
      const updatedPieces = this.moldGroupConfigs[index].pieces.map(piece => ({
        ...piece,
        priority
      }));

      this.moldGroupConfigs[index] = {
        ...this.moldGroupConfigs[index],
        priority,
        pieces: updatedPieces
      };

      // Also update the main pieces array to keep them in sync
      for (const piece of updatedPieces) {
        const mainPieceIndex = this.pieces.findIndex(
          p => p.uniquePieceIdentifier === piece.uniquePieceIdentifier
        );
        if (mainPieceIndex >= 0) {
          this.pieces[mainPieceIndex] = {
            ...this.pieces[mainPieceIndex],
            priority
          };
        }
      }

      this.clearSimulation();
    }
  };

  /**
   * Update linearFt for all pieces in a mold group. Empty string treated as null
   * (no value). Kept as string so the UI input can edit freely; backend parses
   * to a number at panel-write time.
   */
  updateMoldGroupLinearFt = (index: number, linearFt: string | null) => {
    if (index >= 0 && index < this.moldGroupConfigs.length) {
      const normalized = linearFt && linearFt.trim() !== '' ? linearFt.trim() : null;
      const updatedPieces = this.moldGroupConfigs[index].pieces.map(piece => ({
        ...piece,
        linearFt: normalized ?? undefined
      }));

      this.moldGroupConfigs[index] = {
        ...this.moldGroupConfigs[index],
        linearFt: normalized,
        pieces: updatedPieces
      };

      // Keep the main pieces array in sync
      for (const piece of updatedPieces) {
        const mainPieceIndex = this.pieces.findIndex(
          p => p.uniquePieceIdentifier === piece.uniquePieceIdentifier
        );
        if (mainPieceIndex >= 0) {
          this.pieces[mainPieceIndex] = {
            ...this.pieces[mainPieceIndex],
            linearFt: normalized ?? undefined
          };
        }
      }

      this.clearSimulation();
    }
  };

  // ===== Hours Array Helpers (for chip display) =====

  parseHoursToArray = (hoursStr: string): number[] => {
    if (!hoursStr) return [];
    return hoursStr
      .split(",")
      .map((h) => parseFloat(h.trim()))
      .filter((h) => !isNaN(h));
  };

  arrayToHoursString = (hoursArr: number[]): string => {
    return hoursArr.join(",");
  };

  addMoldProductionHourChip = (groupIndex: number) => {
    if (groupIndex >= 0 && groupIndex < this.moldGroupConfigs.length) {
      const currentHours = this.parseHoursToArray(this.moldGroupConfigs[groupIndex].moldProductionHours);
      currentHours.push(8);
      this.moldGroupConfigs[groupIndex] = {
        ...this.moldGroupConfigs[groupIndex],
        moldProductionHours: this.arrayToHoursString(currentHours),
      };
    }
  };

  changeMoldProductionHourChip = (groupIndex: number, chipIndex: number, newValue: number) => {
    if (groupIndex >= 0 && groupIndex < this.moldGroupConfigs.length) {
      const currentHours = this.parseHoursToArray(this.moldGroupConfigs[groupIndex].moldProductionHours);
      if (chipIndex >= 0 && chipIndex < currentHours.length) {
        currentHours[chipIndex] = newValue;
        this.moldGroupConfigs[groupIndex] = {
          ...this.moldGroupConfigs[groupIndex],
          moldProductionHours: this.arrayToHoursString(currentHours),
        };
      }
    }
  };

  removeMoldProductionHourChip = (groupIndex: number, chipIndex: number) => {
    if (groupIndex >= 0 && groupIndex < this.moldGroupConfigs.length) {
      const currentHours = this.parseHoursToArray(this.moldGroupConfigs[groupIndex].moldProductionHours);
      if (chipIndex >= 0 && chipIndex < currentHours.length) {
        currentHours.splice(chipIndex, 1);
        this.moldGroupConfigs[groupIndex] = {
          ...this.moldGroupConfigs[groupIndex],
          moldProductionHours: this.arrayToHoursString(currentHours),
        };
      }
    }
  };

  // Legacy aliases for backward compatibility
  addHourChip = (groupIndex: number) => this.addMoldProductionHourChip(groupIndex);
  changeHourChip = (groupIndex: number, chipIndex: number, newValue: number) => this.changeMoldProductionHourChip(groupIndex, chipIndex, newValue);
  removeHourChip = (groupIndex: number, chipIndex: number) => this.removeMoldProductionHourChip(groupIndex, chipIndex);

  // ===== Wizard Navigation =====

  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    this.activeStep = Math.min(this.activeStep + 1, 3); // Max 4 steps (0-3)
  };

  prevStep = () => {
    this.activeStep = Math.max(this.activeStep - 1, 0);
  };

  // ===== Schedule Simulation =====

  /**
   * Run schedule simulation without creating any database records
   * Returns chips for calendar preview and any conflicts
   */
  simulateSchedule = async (): Promise<void> => {
    if (this.pieces.length === 0 || this.moldGroupConfigs.length === 0) {
      console.log('[ProductionScheduleUploadViewStore] Cannot simulate - no pieces or mold configs');
      return;
    }

    this.simulationLoading = true;
    this.error = null;

    try {
      const csvUploadService = this.rootStore.app.service('csv-upload');

      const result: ScheduleSimulation = await csvUploadService.create({
        action: 'simulate',
        pieces: this.pieces,
        moldGroups: this.moldGroupConfigs.map((config) => ({
          moldNumber: config.moldNumber,
          moldCount: config.moldCount,
          moldProductionHours: config.moldProductionHours,
          // Prefer per-mold durations from the sheet; fall back to view-store defaults
          setupHours: config.setupHours || this.setupHours,
          castingHours: config.castingHours || this.castingHours,
          finishingHours: config.finishingHours || this.finishingHours,
          productionArea: config.productionArea || null,
          priority: config.priority ?? null,
          scheduleMode: config.scheduleMode || 'shipDate',
          startDate: config.startDate,
          setupToCastingGap: config.setupToCastingGap ?? 0,
          shipDate: config.pieces.length > 0
            ? [...new Set(config.pieces.map(p => p.shippingDate).filter(Boolean))].sort()[0]
            : undefined,
        })),
      });

      console.log('[ProductionScheduleUploadViewStore] Simulation result:', result);

      runInAction(() => {
        this.simulationData = result;
        this.simulationLoading = false;
      });
    } catch (err: any) {
      console.error('[ProductionScheduleUploadViewStore] Simulation error:', err);
      runInAction(() => {
        this.error = err.message || 'Failed to simulate schedule';
        this.simulationLoading = false;
      });
    }
  };

  /**
   * Clear simulation data (call when mold config changes)
   */
  clearSimulation = () => {
    this.simulationData = null;
  };

  // ===== CSV Upload - Backend API =====

  /**
   * Parse CSV by sending to backend preview endpoint
   */
  parseCSV = async (csvText: string, fileName: string) => {
    this.setUploading(true);
    this.setUploadProgress(50);
    this.setError(null);

    console.log('[ProductionScheduleUploadViewStore] parseCSV called with', fileName);
    console.log('[ProductionScheduleUploadViewStore] CSV content preview:', csvText.substring(0, 500));

    // Extract configuration from CSV if present (config lines start with #CONFIG:)
    const cleanedCsvText = this.extractConfigFromCSV(csvText);

    try {
      const csvUploadService = this.rootStore.app.service('csv-upload');
      console.log('[ProductionScheduleUploadViewStore] Got csv-upload service:', !!csvUploadService);

      // Use create with action: 'preview' - standard FeathersJS pattern for custom actions
      const previewResponse: PreviewResponse = await csvUploadService.create({
        action: 'preview',
        csvData: cleanedCsvText,
        bidNumber: this.bidNumber || undefined,
      });

      console.log('[ProductionScheduleUploadViewStore] Preview response:', previewResponse);

      runInAction(() => {
        this.fileName = fileName;
        this.rawCsvData = csvText;
        this.pieces = previewResponse.pieces || [];
        this.validationErrors = previewResponse.errors || [];

        // Check for validation errors from backend
        if (this.validationErrors.length > 0) {
          const errorMessages = this.validationErrors.map(e => `Row ${e.row}: ${e.message}`).join('; ');
          this.error = `CSV Validation Errors: ${errorMessages}`;
        }

        // Convert moldGroups to editable configs. If the sheet carried per-day
        // phase columns (M1..M11 / S1..S2 / C1 / F1..F3), prefill those into the
        // mold config; otherwise fall back to the view-store defaults so the UI
        // remains usable for legacy CSVs.
        const firstPieceStartDate = previewResponse.pieces?.find(p => p.startDate)?.startDate;
        const anyPieceHasStartDate = !!firstPieceStartDate;

        // When no piece carries a start date, every group silently falls back
        // to 'shipDate' mode — and that mode does NOT schedule from the ship
        // date, it schedules from TODAY (csv-upload.class.ts:1111). That is
        // how Josh's 2027 stairs landed in 2026 on 2026-10-01 with nothing on
        // screen to say so. Record it so the wizard can warn.
        runInAction(() => {
          this.noStartDateColumn = !anyPieceHasStartDate;
        });

        this.moldGroupConfigs = (previewResponse.moldGroups || []).map((group) => {
          const firstPieceInGroup = group.pieces[0];
          const groupStartDate = firstPieceInGroup?.startDate || firstPieceStartDate;
          return {
            moldNumber: group.moldNumber,
            moldCount: group.suggestedMoldCount,
            moldProductionHours: group.duration || '8',
            setupHours: group.setupHours || this.setupHours,
            castingHours: group.castingHours || this.castingHours,
            finishingHours: group.finishingHours || this.finishingHours,
            pieceCount: group.pieceCount,
            pieces: group.pieces,
            productionArea: '',
            priority: group.pieces[0]?.priority ?? null,
            linearFt: group.pieces[0]?.linearFt ?? null,
            scheduleMode: anyPieceHasStartDate ? 'startDate' : 'shipDate',
            startDate: groupStartDate || undefined,
          } as MoldGroupConfig;
        });

        // Apply any pending mold configs from imported config CSV
        this.applyPendingMoldConfigs();

        console.log('[ProductionScheduleUploadViewStore] Parsed pieces:', this.pieces.length);
        console.log('[ProductionScheduleUploadViewStore] Mold groups:', this.moldGroupConfigs.length);

        this.uploading = false;
        this.uploadProgress = 100;
      });
    } catch (err: any) {
      console.error('[ProductionScheduleUploadViewStore] parseCSV error:', err);
      runInAction(() => {
        this.error = err.message || err.toString() || 'Failed to parse CSV';
        this.uploading = false;
        this.fileName = fileName; // Still set filename so user can see what was uploaded
      });
    }
  };

  /**
   * Parse an .xlsx file by converting its first worksheet to CSV text and
   * handing it to the existing parseCSV() flow. The backend parser is the
   * single source of truth for header detection and field mapping.
   */
  parseXLSX = async (file: File, fileName: string): Promise<void> => {
    this.setUploading(true);
    this.setUploadProgress(25);
    this.setError(null);

    try {
      const ExcelJS = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      const arrayBuffer = await file.arrayBuffer();
      await workbook.xlsx.load(arrayBuffer);

      const worksheet = workbook.worksheets[0];
      if (!worksheet) {
        throw new Error('No worksheets found in the uploaded .xlsx file.');
      }

      const escapeCell = (v: unknown): string => {
        if (v === null || v === undefined) return '';
        // ExcelJS can return { text, richText } for rich cells, { formula, result } for formulas, or Date
        let s: string;
        if (v instanceof Date) {
          // Normalize to yyyy-MM-dd so the backend parser's normalizeDate keeps working
          const y = v.getFullYear();
          const m = String(v.getMonth() + 1).padStart(2, '0');
          const d = String(v.getDate()).padStart(2, '0');
          s = `${y}-${m}-${d}`;
        } else if (typeof v === 'object') {
          const obj = v as { text?: string; result?: unknown; richText?: Array<{ text: string }> };
          if (obj.richText) {
            s = obj.richText.map(r => r.text).join('');
          } else if (obj.result !== undefined) {
            s = String(obj.result);
          } else if (obj.text !== undefined) {
            s = obj.text;
          } else {
            s = String(v);
          }
        } else {
          s = String(v);
        }
        // CSV-escape
        if (s.includes(',') || s.includes('"') || s.includes('\n')) {
          return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
      };

      const lines: string[] = [];
      for (let rowIdx = 1; rowIdx <= worksheet.rowCount; rowIdx++) {
        const row = worksheet.getRow(rowIdx);
        const cells: string[] = [];
        for (let colIdx = 1; colIdx <= worksheet.columnCount; colIdx++) {
          cells.push(escapeCell(row.getCell(colIdx).value));
        }
        // Drop rows that are entirely empty
        if (cells.some(c => c && c !== '""')) {
          lines.push(cells.join(','));
        }
      }

      const csvText = lines.join('\n');
      this.setUploadProgress(40);
      await this.parseCSV(csvText, fileName);
    } catch (err: any) {
      console.error('[ProductionScheduleUploadViewStore] parseXLSX error:', err);
      runInAction(() => {
        this.error = err.message || err.toString() || 'Failed to parse .xlsx';
        this.uploading = false;
        this.fileName = fileName;
      });
    }
  };

  // ===== Save Logic - Backend API =====

  save = async (): Promise<boolean> => {
    const { moldGroupConfigs, projectId, pieces, schedulingMode, manualPlacements } = this;

    if (!projectId) {
      this.error = "Please select a project.";
      return false;
    }

    const groupsMissingHours = moldGroupConfigs.filter((group) => !group.moldProductionHours?.trim());
    if (groupsMissingHours.length > 0) {
      const missingNames = groupsMissingHours.map((g) => g.moldNumber).join(", ");
      this.error = `Please set Mold Production Hours for all molds. Missing: ${missingNames}`;
      return false;
    }

    // In manual mode, ensure all pieces are placed
    if (schedulingMode === 'manual' && manualPlacements.size !== pieces.length) {
      this.error = `Please schedule all pieces. ${manualPlacements.size} of ${pieces.length} pieces scheduled.`;
      return false;
    }

    // Enforce unique (panelMark, pourNumber) within the project. The DB has a
    // UNIQUE(projectId, panelMark, pourNumber) constraint — surface duplicate
    // conflicts here so the user sees a friendly message instead of a generic
    // 500 partway through the import.
    const markValidationError = await this.validateMarkUniqueness();
    if (markValidationError) {
      this.error = markValidationError;
      return false;
    }

    this.saving = true;
    this.error = null;

    try {
      const csvUploadService = this.rootStore.app.service('csv-upload');

      // Build the confirm request - use create with action: 'confirm'
      // Include manualPlacements if in manual mode
      const confirmRequest: any = {
        action: 'confirm',
        projectId: parseInt(projectId, 10),
        pieces: pieces,
        moldGroups: moldGroupConfigs.map((config) => ({
          moldNumber: config.moldNumber,
          moldCount: config.moldCount,
          moldProductionHours: config.moldProductionHours,
          // Prefer per-mold durations from the sheet; fall back to view-store defaults
          setupHours: config.setupHours || this.setupHours,
          castingHours: config.castingHours || this.castingHours,
          finishingHours: config.finishingHours || this.finishingHours,
          productionArea: config.productionArea || null,
          priority: config.priority ?? null,
          scheduleMode: config.scheduleMode || 'shipDate',
          startDate: config.startDate,
          setupToCastingGap: config.setupToCastingGap ?? 0,
          shipDate: config.pieces.length > 0
            ? [...new Set(config.pieces.map(p => p.shippingDate).filter(Boolean))].sort()[0]
            : undefined,
        })),
      };

      // Add manual placements if in manual mode
      if (schedulingMode === 'manual' && manualPlacements.size > 0) {
        confirmRequest.manualPlacements = Array.from(manualPlacements.values());
      }

      const result = await csvUploadService.create(confirmRequest);

      runInAction(() => {
        this.saving = false;
        this.success = `Successfully created ${result.createdPieces} panels, ${result.createdMoldInstances} mold instances, and scheduled ${result.scheduledItems} items!`;
      });

      // Refresh stores to show new data
      const panelsStore = (this.rootStore as any).panelsStore;
      const moldsStore = (this.rootStore as any).moldsStore;
      if (panelsStore?.find) panelsStore.find();
      if (moldsStore?.find) moldsStore.find();

      // Refresh the production schedule grid store so newly uploaded chips appear immediately
      const gridStore = (this.rootStore as any).productionScheduleGridStore;
      if (gridStore?.refreshSchedulesSilent) {
        gridStore.refreshSchedulesSilent();
      }

      // Reset after successful import (with delay for user to see success message)
      setTimeout(() => {
        runInAction(() => {
          this.reset();
          this.rootStore.routerStore?.closeDialog();
        });
      }, 2000);

      return true;
    } catch (err: any) {
      console.error("Save error:", err);
      runInAction(() => {
        this.saving = false;
        this.error = err.message || "Failed to save data";
      });
      return false;
    }
  };

  /**
   * Validate (panelMark, pourNumber) uniqueness for this CSV against:
   *   1. Other rows in the same CSV (intra-batch duplicates)
   *   2. Existing panels already saved to the same project
   *
   * Returns an error string when a conflict is found, or null when clean.
   * Surfaces up to 5 specific conflicts so the user can fix them, not just a
   * "something's wrong" message.
   */
  private validateMarkUniqueness = async (): Promise<string | null> => {
    const projectId = parseInt(this.projectId, 10);
    if (!projectId || this.pieces.length === 0) return null;

    const keyOf = (mark: string, pour: string) =>
      `${mark.trim().toLowerCase()}::${(pour || '1').trim() || '1'}`;

    // 1) Intra-CSV duplicates
    const seen = new Map<string, number>();
    const intraConflicts: string[] = [];
    for (const piece of this.pieces) {
      const mark = (piece.mark || '').trim();
      if (!mark) continue;
      const key = keyOf(mark, piece.pourNumber || '1');
      const prevCount = seen.get(key) || 0;
      seen.set(key, prevCount + 1);
      if (prevCount === 1) {
        // Report on the second occurrence so we list each conflicting mark once
        const pourLabel = (piece.pourNumber || '1') === '1' ? '' : ` (pour ${piece.pourNumber})`;
        intraConflicts.push(`${mark}${pourLabel}`);
      }
    }
    if (intraConflicts.length > 0) {
      const shown = intraConflicts.slice(0, 5).join(', ');
      const extra = intraConflicts.length > 5 ? `, plus ${intraConflicts.length - 5} more` : '';
      return `Duplicate panel marks in the uploaded file: ${shown}${extra}. ` +
        `Each (mark, pour number) must be unique within a project.`;
    }

    // 2) Cross-CSV: conflicts against existing panels in the project
    try {
      const panelsService = this.rootStore.app.service('panels');
      const existing = await panelsService.find({
        query: { projectId, $limit: 1000 },
      });
      const existingArr: Array<{ panelMark?: string; pourNumber?: string | null }> =
        Array.isArray(existing) ? existing : (existing?.data || []);
      const existingKeys = new Set<string>();
      for (const p of existingArr) {
        const mark = (p.panelMark || '').trim();
        if (!mark) continue;
        existingKeys.add(keyOf(mark, (p.pourNumber as string | null) || '1'));
      }
      const crossConflicts: string[] = [];
      for (const piece of this.pieces) {
        const mark = (piece.mark || '').trim();
        if (!mark) continue;
        const key = keyOf(mark, piece.pourNumber || '1');
        if (existingKeys.has(key)) {
          const pourLabel = (piece.pourNumber || '1') === '1' ? '' : ` (pour ${piece.pourNumber})`;
          crossConflicts.push(`${mark}${pourLabel}`);
        }
      }
      if (crossConflicts.length > 0) {
        const shown = crossConflicts.slice(0, 5).join(', ');
        const extra = crossConflicts.length > 5 ? `, plus ${crossConflicts.length - 5} more` : '';
        return `These panel marks already exist in the project: ${shown}${extra}. ` +
          `Either rename them in the file, or use the Add Piece dialog's Reissue tab if you intend to re-pour an existing piece.`;
      }
    } catch (err) {
      console.warn('[ProductionScheduleUploadViewStore] Could not pre-check existing panels for mark uniqueness:', err);
      // Don't block the import on a transient panels-find failure — the DB
      // constraint will still catch any actual duplicate.
    }

    return null;
  };

  // ===== Reset =====

  removeFile = () => {
    this.fileName = null;
    this.rawCsvData = "";
    this.pieces = [];
    this.moldGroupConfigs = [];
    this.validationErrors = [];
    this.uploadProgress = 0;
    this.error = null;
    this.success = null;
  };

  // ===== Configuration Export/Import =====

  /**
   * Export current configuration and pieces as a CSV file
   * Config lines are prefixed with #CONFIG: so they can be detected on re-import
   */
  exportConfigurationCSV = () => {
    const lines: string[] = [];

    // Add configuration header lines
    lines.push(`#CONFIG:setupHours,${this.setupHours}`);
    lines.push(`#CONFIG:castingHours,${this.castingHours}`);
    lines.push(`#CONFIG:finishingHours,${this.finishingHours}`);
    if (this.projectId) lines.push(`#CONFIG:projectId,${this.projectId}`);
    if (this.bidNumber) lines.push(`#CONFIG:bidNumber,${this.bidNumber}`);

    // Add mold group configs (including ship date from earliest piece)
    for (const group of this.moldGroupConfigs) {
      // Get the earliest ship date from pieces in this group
      const shipDate = group.pieces.length > 0
        ? [...new Set(group.pieces.map(p => p.shippingDate))].sort()[0]
        : '';
      lines.push(`#CONFIG:MOLD:${group.moldNumber},moldCount=${group.moldCount},moldProductionHours=${group.moldProductionHours || ''},productionArea=${group.productionArea || ''},shipDate=${shipDate},priority=${group.priority ?? ''}`);
    }

    // Add CSV header row
    lines.push('Count,Type,Mold,Location,Mark,Volume,Weight,Bid Number,Pour Number,Shipping Date,Product Code,Priority');

    // Add piece data rows
    for (const piece of this.pieces) {
      // Escape values that might contain commas
      const escapeCSV = (val: string | number | null | undefined) => {
        const str = String(val ?? '');
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      lines.push([
        piece.count,
        escapeCSV(piece.type),
        escapeCSV(piece.mold),
        escapeCSV(piece.location),
        escapeCSV(piece.mark),
        escapeCSV(piece.volume),
        escapeCSV(piece.weight),
        escapeCSV(piece.bidNumber),
        escapeCSV(piece.pourNumber),
        escapeCSV(piece.shippingDate),
        escapeCSV(piece.productCode),
        piece.priority ?? '',
      ].join(','));
    }

    // Trigger download
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `production-schedule-config-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /**
   * Extract configuration from CSV content (lines starting with #CONFIG:)
   * Returns the cleaned CSV content without config lines
   */
  private extractConfigFromCSV = (csvContent: string): string => {
    const lines = csvContent.split('\n');
    const configLines = lines.filter(l => l.startsWith('#CONFIG:'));
    const dataLines = lines.filter(l => !l.startsWith('#CONFIG:'));

    // Clear any previous pending configs
    this.pendingMoldConfigs.clear();

    // Parse config lines
    for (const line of configLines) {
      const content = line.replace('#CONFIG:', '').trim();

      if (content.startsWith('setupHours,')) {
        this.setupHours = content.split(',').slice(1).join(',') || '2';
      } else if (content.startsWith('castingHours,')) {
        this.castingHours = content.split(',').slice(1).join(',') || '1';
      } else if (content.startsWith('finishingHours,')) {
        this.finishingHours = content.split(',').slice(1).join(',') || '2,2';
      } else if (content.startsWith('projectId,')) {
        this.projectId = content.split(',')[1] || '';
      } else if (content.startsWith('bidNumber,')) {
        this.bidNumber = content.split(',')[1] || '';
      } else if (content.startsWith('MOLD:')) {
        // Parse mold config: MOLD:501,moldCount=2,moldProductionHours=8,productionArea=area,shipDate=2024-01-15
        const moldPart = content.replace('MOLD:', '');
        const parts = moldPart.split(',');
        const moldNumber = parts[0];

        let moldCount = 1;
        let moldProductionHours = '8';  // Default 8 hours for mold production
        let productionArea = '';
        let shipDate = '';
        let priority: number | null = null;

        for (let i = 1; i < parts.length; i++) {
          const part = parts[i];
          if (part.startsWith('moldCount=')) {
            moldCount = parseInt(part.replace('moldCount=', ''), 10) || 1;
          } else if (part.startsWith('moldProductionHours=')) {
            // moldProductionHours may contain commas (e.g., "8,8,2"), so extract carefully
            const hoursStart = moldPart.indexOf('moldProductionHours=') + 'moldProductionHours='.length;
            const hoursEnd = moldPart.indexOf(',productionArea=');
            if (hoursEnd > hoursStart) {
              moldProductionHours = moldPart.substring(hoursStart, hoursEnd) || '8';
            }
          } else if (part.startsWith('castingHours=')) {
            // Legacy support: treat castingHours as moldProductionHours
            const hoursStart = moldPart.indexOf('castingHours=') + 'castingHours='.length;
            const hoursEnd = moldPart.indexOf(',productionArea=');
            if (hoursEnd > hoursStart) {
              moldProductionHours = moldPart.substring(hoursStart, hoursEnd) || '8';
            }
          } else if (part.startsWith('productionArea=')) {
            // productionArea is between productionArea= and ,shipDate=
            const areaStart = moldPart.indexOf('productionArea=') + 'productionArea='.length;
            const areaEnd = moldPart.indexOf(',shipDate=');
            if (areaEnd > areaStart) {
              productionArea = moldPart.substring(areaStart, areaEnd) || '';
            } else {
              productionArea = part.replace('productionArea=', '');
            }
          } else if (part.startsWith('shipDate=')) {
            // shipDate is between shipDate= and ,priority= (or end of string)
            const dateStart = moldPart.indexOf('shipDate=') + 'shipDate='.length;
            const dateEnd = moldPart.indexOf(',priority=');
            if (dateEnd > dateStart) {
              shipDate = moldPart.substring(dateStart, dateEnd) || '';
            } else {
              shipDate = part.replace('shipDate=', '');
            }
          } else if (part.startsWith('priority=')) {
            const val = part.replace('priority=', '');
            priority = val ? parseInt(val, 10) : null;
            if (isNaN(priority as number)) priority = null;
          }
        }

        this.pendingMoldConfigs.set(moldNumber, { moldCount, moldProductionHours, productionArea, shipDate, priority });
      }
    }

    console.log('[ProductionScheduleUploadViewStore] Extracted config from CSV:', {
      setupHours: this.setupHours,
      castingHours: this.castingHours,
      finishingHours: this.finishingHours,
      projectId: this.projectId,
      bidNumber: this.bidNumber,
      pendingMoldConfigs: Array.from(this.pendingMoldConfigs.entries()),
    });

    // Return cleaned data lines for normal CSV parsing
    return dataLines.join('\n');
  };

  /**
   * Apply pending mold configs to moldGroupConfigs after they're built from pieces
   */
  private applyPendingMoldConfigs = () => {
    if (this.pendingMoldConfigs.size === 0) return;

    for (const group of this.moldGroupConfigs) {
      const pending = this.pendingMoldConfigs.get(group.moldNumber);
      if (pending) {
        group.moldCount = pending.moldCount;
        group.moldProductionHours = pending.moldProductionHours || group.moldProductionHours;
        group.productionArea = pending.productionArea || '';
        group.priority = pending.priority;

        // Apply ship date and priority to all pieces in this group if provided
        if (pending.shipDate || pending.priority != null) {
          group.pieces = group.pieces.map(piece => ({
            ...piece,
            ...(pending.shipDate && { shippingDate: pending.shipDate }),
            ...(pending.priority != null && { priority: pending.priority }),
          }));

          // Also update the main pieces array
          for (const piece of group.pieces) {
            const mainPieceIndex = this.pieces.findIndex(
              p => p.uniquePieceIdentifier === piece.uniquePieceIdentifier
            );
            if (mainPieceIndex >= 0) {
              this.pieces[mainPieceIndex] = {
                ...this.pieces[mainPieceIndex],
                ...(pending.shipDate && { shippingDate: pending.shipDate }),
                ...(pending.priority != null && { priority: pending.priority }),
              };
            }
          }
        }
      }
    }

    console.log('[ProductionScheduleUploadViewStore] Applied pending mold configs');
    this.pendingMoldConfigs.clear();
  };

  // ===== Manual Scheduling Methods =====

  setSchedulingMode = (mode: SchedulingMode) => {
    this.schedulingMode = mode;
    if (mode === 'auto' && !this.simulationData && this.pieces.length > 0) {
      this.simulateSchedule();
    }
  };

  setDraggedPieceIndex = (pieceIndex: number | null) => {
    this.draggedPieceIndex = pieceIndex;
  };

  /**
   * Place a piece on a specific date for a mold instance
   */
  placePiece = (pieceIndex: number, moldInstanceCode: string, startDate: string) => {
    const piece = this.pieces[pieceIndex];
    if (!piece) return;

    this.manualPlacements.set(pieceIndex, {
      pieceIndex,
      pieceIdentifier: piece.uniquePieceIdentifier,
      moldInstanceCode,
      startDate,
    });
    this.draggedPieceIndex = null;
  };

  /**
   * Remove a piece placement
   */
  removePlacement = (pieceIndex: number) => {
    this.manualPlacements.delete(pieceIndex);
  };

  /**
   * Move a single chip to a new date (for manual adjustments after initial placement)
   */
  moveChip = (pieceIndex: number, phase: string, dayNumber: number, newDate: string) => {
    const placement = this.manualPlacements.get(pieceIndex);
    if (!placement) return;

    const piece = this.pieces[pieceIndex];
    if (!piece) return;

    // Initialize chipPositions if not present (calculate from startDate)
    if (!placement.chipPositions) {
      const chips = this.calculateChipsForPlacement(piece, placement);
      placement.chipPositions = chips.map(c => ({
        phase: c.phase,
        dayNumber: c.dayNumber,
        date: c.date,
      }));
    }

    // Find and update the specific chip
    const chipIndex = placement.chipPositions.findIndex(
      c => c.phase === phase && c.dayNumber === dayNumber
    );
    if (chipIndex !== -1) {
      placement.chipPositions[chipIndex] = {
        ...placement.chipPositions[chipIndex],
        date: newDate,
      };
    }

    // Update the placement in the map (triggers reactivity)
    this.manualPlacements.set(pieceIndex, { ...placement });
  };

  /**
   * Clear all manual placements
   */
  clearAllPlacements = () => {
    this.manualPlacements.clear();
  };

  /**
   * Get unscheduled pieces with their indices (those without manual placements)
   */
  get unscheduledPiecesWithIndex(): { piece: ParsedPiece; index: number }[] {
    return this.pieces
      .map((piece, index) => ({ piece, index }))
      .filter(({ index }) => !this.manualPlacements.has(index));
  }

  /**
   * Get scheduled pieces with their indices (those with manual placements)
   */
  get scheduledPiecesWithIndex(): { piece: ParsedPiece; index: number }[] {
    return this.pieces
      .map((piece, index) => ({ piece, index }))
      .filter(({ index }) => this.manualPlacements.has(index));
  }

  /**
   * Check if all pieces have been placed
   */
  get allPiecesPlaced(): boolean {
    return this.pieces.length > 0 && this.manualPlacements.size === this.pieces.length;
  }

  /**
   * Load existing capacity from database (scheduled items already in system)
   */
  loadExistingCapacity = async (): Promise<void> => {
    try {
      const csvUploadService = this.rootStore.app.service('csv-upload');

      // Call simulate with empty pieces to just get capacity data
      const result = await csvUploadService.create({
        action: 'simulate',
        pieces: [],
        moldGroups: [],
      });

      runInAction(() => {
        this.existingCapacity = result.capacityByDate || [];
      });
    } catch (err) {
      console.error('[ProductionScheduleUploadViewStore] Failed to load existing capacity:', err);
    }
  };

  /**
   * Generate mold instance codes for the configured molds
   */
  get generatedMoldInstances(): { moldNumber: string; instanceCode: string; instanceNumber: number }[] {
    const instances: { moldNumber: string; instanceCode: string; instanceNumber: number }[] = [];
    for (const group of this.moldGroupConfigs) {
      for (let i = 1; i <= group.moldCount; i++) {
        instances.push({
          moldNumber: group.moldNumber,
          instanceCode: `${group.moldNumber}.${i}`,
          instanceNumber: i,
        });
      }
    }
    return instances;
  }

  /**
   * Check if a mold instance already has another piece placed on it
   * (used to determine if moldProduction chips should be skipped)
   */
  isMoldInstanceOccupied = (moldInstanceCode: string, excludePieceIndex?: number): boolean => {
    for (const [pieceIndex, placement] of this.manualPlacements) {
      if (excludePieceIndex !== undefined && pieceIndex === excludePieceIndex) continue;
      if (placement.moldInstanceCode === moldInstanceCode) return true;
    }
    return false;
  };

  /**
   * Calculate chips for a manually placed piece
   * Uses the same forward calculation as addSingle
   * Skips moldProduction chips if another piece is already on the same mold instance
   */
  calculateChipsForPlacement = (piece: ParsedPiece, placement: ManualPlacement, skipMoldProduction?: boolean): ScheduleChip[] => {
    const chips: ScheduleChip[] = [];
    const group = this.moldGroupConfigs.find(g => g.moldNumber === piece.mold);
    if (!group) return chips;

    // Parse hours arrays
    const moldProdHours = this.parseHoursToArray(group.moldProductionHours);
    const setupHoursArr = this.parseHoursToArray(this.setupHours);
    const castingHoursArr = this.parseHoursToArray(this.castingHours);
    const finishingHoursArr = this.parseHoursToArray(this.finishingHours);

    // Determine if we should skip mold production (shared per mold, only first piece gets it)
    const shouldSkipMoldProd = skipMoldProduction ?? this.isMoldInstanceOccupied(placement.moldInstanceCode, placement.pieceIndex);

    let currentDate = new Date(placement.startDate + 'T00:00:00');

    const addBusinessDays = (date: Date, days: number): Date => {
      const result = new Date(date);
      let added = 0;
      while (added < days) {
        result.setDate(result.getDate() + 1);
        const dayOfWeek = result.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          added++;
        }
      }
      return result;
    };

    const formatDate = (d: Date): string => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Mold Production chips (only for the first piece on this mold instance)
    if (!shouldSkipMoldProd) {
      for (let i = 0; i < moldProdHours.length; i++) {
        chips.push({
          panelId: 0, // Will be assigned on confirm
          panelIdentifier: piece.uniquePieceIdentifier,
          date: formatDate(currentDate),
          phase: 'moldProduction',
          dayNumber: i + 1,
          hours: moldProdHours[i],
          moldInstanceCode: placement.moldInstanceCode,
        });
        if (i < moldProdHours.length - 1) {
          currentDate = addBusinessDays(currentDate, 1);
        }
      }
      // Mold production always occupies its own day(s) — advance to next business day for setup
      currentDate = addBusinessDays(currentDate, 1);
    }
    for (let i = 0; i < setupHoursArr.length; i++) {
      chips.push({
        panelId: 0,
        panelIdentifier: piece.uniquePieceIdentifier,
        date: formatDate(currentDate),
        phase: 'setup',
        dayNumber: i + 1,
        hours: setupHoursArr[i],
        moldInstanceCode: placement.moldInstanceCode,
      });
      if (i < setupHoursArr.length - 1) {
        currentDate = addBusinessDays(currentDate, 1);
      }
    }

    // Casting chips (same day as last setup if setup is single day, otherwise next day)
    if (setupHoursArr.length > 1) {
      currentDate = addBusinessDays(currentDate, 1);
    }
    for (let i = 0; i < castingHoursArr.length; i++) {
      chips.push({
        panelId: 0,
        panelIdentifier: piece.uniquePieceIdentifier,
        date: formatDate(currentDate),
        phase: 'casting',
        dayNumber: i + 1,
        hours: castingHoursArr[i],
        moldInstanceCode: placement.moldInstanceCode,
      });
      if (i < castingHoursArr.length - 1) {
        currentDate = addBusinessDays(currentDate, 1);
      }
    }

    // Finishing chips (next business day after casting - overnight cure)
    currentDate = addBusinessDays(currentDate, 1);
    for (let i = 0; i < finishingHoursArr.length; i++) {
      chips.push({
        panelId: 0,
        panelIdentifier: piece.uniquePieceIdentifier,
        date: formatDate(currentDate),
        phase: 'finishing',
        dayNumber: i + 1,
        hours: finishingHoursArr[i],
        moldInstanceCode: placement.moldInstanceCode,
      });
      if (i < finishingHoursArr.length - 1) {
        currentDate = addBusinessDays(currentDate, 1);
      }
    }

    // Shipping chip (next business day after finishing)
    currentDate = addBusinessDays(currentDate, 1);
    chips.push({
      panelId: 0,
      panelIdentifier: piece.uniquePieceIdentifier,
      date: formatDate(currentDate),
      phase: 'shipping',
      dayNumber: 1,
      hours: 0,
      moldInstanceCode: placement.moldInstanceCode,
    });

    return chips;
  };

  /**
   * Get all chips from manual placements
   * Uses chipPositions if available (after manual adjustments), otherwise calculates from startDate
   * Mold production is shared per mold instance — only the first piece placed gets moldProduction chips
   */
  get manualChips(): ScheduleChip[] {
    const allChips: ScheduleChip[] = [];
    // Track which mold instances already have a piece to skip moldProduction for subsequent ones
    const moldInstancesWithPiece = new Set<string>();

    for (const [pieceIndex, placement] of this.manualPlacements) {
      const piece = this.pieces[pieceIndex];
      if (!piece) continue;

      const isFirstOnMold = !moldInstancesWithPiece.has(placement.moldInstanceCode);
      moldInstancesWithPiece.add(placement.moldInstanceCode);

      if (placement.chipPositions && placement.chipPositions.length > 0) {
        // Use stored chip positions (after manual adjustments)
        const group = this.moldGroupConfigs.find(g => g.moldNumber === piece.mold);
        if (!group) continue;

        const moldProdHours = this.parseHoursToArray(group.moldProductionHours);
        const setupHoursArr = this.parseHoursToArray(this.setupHours);
        const castingHoursArr = this.parseHoursToArray(this.castingHours);
        const finishingHoursArr = this.parseHoursToArray(this.finishingHours);

        for (const chipPos of placement.chipPositions) {
          // Skip moldProduction chips for non-first pieces on the mold instance
          if (chipPos.phase === 'moldProduction' && !isFirstOnMold) continue;

          // Determine hours based on phase and day number
          let hours = 0;
          if (chipPos.phase === 'moldProduction') {
            hours = moldProdHours[chipPos.dayNumber - 1] || 0;
          } else if (chipPos.phase === 'setup') {
            hours = setupHoursArr[chipPos.dayNumber - 1] || 0;
          } else if (chipPos.phase === 'casting') {
            hours = castingHoursArr[chipPos.dayNumber - 1] || 0;
          } else if (chipPos.phase === 'finishing') {
            hours = finishingHoursArr[chipPos.dayNumber - 1] || 0;
          }

          allChips.push({
            panelId: 0,
            panelIdentifier: piece.uniquePieceIdentifier,
            date: chipPos.date,
            phase: chipPos.phase,
            dayNumber: chipPos.dayNumber,
            hours,
            moldInstanceCode: placement.moldInstanceCode,
          });
        }
      } else {
        // Calculate chips from startDate (initial placement)
        // Pass skipMoldProduction flag for non-first pieces on this mold instance
        const chips = this.calculateChipsForPlacement(piece, placement, !isFirstOnMold);
        allChips.push(...chips);
      }
    }
    return allChips;
  }

  /**
   * Get date range for manual placements
   */
  get manualDateRange(): { startDate: string; endDate: string } {
    const chips = this.manualChips;
    if (chips.length === 0) {
      // Default to 60 days from today
      const today = new Date();
      const endDate = new Date(today);
      endDate.setDate(endDate.getDate() + 60);
      return {
        startDate: today.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
      };
    }
    const dates = chips.map(c => c.date).sort();
    return {
      startDate: dates[0],
      endDate: dates[dates.length - 1],
    };
  }

  /**
   * Calculate capacity by date combining existing capacity and manual placements
   */
  get combinedCapacityByDate(): DayCapacity[] {
    // Start with existing capacity
    const capacityMap = new Map<string, DayCapacity>();
    for (const cap of this.existingCapacity) {
      capacityMap.set(cap.date, { ...cap });
    }

    // Add hours from manual placements
    for (const chip of this.manualChips) {
      if (chip.phase === 'shipping') continue; // Shipping doesn't use capacity

      let dayCap = capacityMap.get(chip.date);
      if (!dayCap) {
        // Create new day capacity entry
        dayCap = {
          date: chip.date,
          availableManHours: 72, // Default total (16+16+24+16)
          allocatedManHours: 0,
          remainingManHours: 72,
          utilizationPercent: 0,
          isHoliday: false,
          isWeekend: false,
          departmentCapacity: {
            moldProduction: { available: 16, allocated: 0, remaining: 16, utilizationPercent: 0, isOverCapacity: false },
            setup: { available: 16, allocated: 0, remaining: 16, utilizationPercent: 0, isOverCapacity: false },
            casting: { available: 24, allocated: 0, remaining: 24, utilizationPercent: 0, isOverCapacity: false },
            finishing: { available: 16, allocated: 0, remaining: 16, utilizationPercent: 0, isOverCapacity: false },
          },
        };
        capacityMap.set(chip.date, dayCap);
      }

      // Add chip hours to appropriate department
      const dept = chip.phase as keyof NonNullable<DayCapacity['departmentCapacity']>;
      if (dayCap.departmentCapacity && dayCap.departmentCapacity[dept]) {
        dayCap.departmentCapacity[dept].allocated += chip.hours;
        dayCap.departmentCapacity[dept].remaining = dayCap.departmentCapacity[dept].available - dayCap.departmentCapacity[dept].allocated;
        dayCap.departmentCapacity[dept].utilizationPercent = (dayCap.departmentCapacity[dept].allocated / dayCap.departmentCapacity[dept].available) * 100;
        dayCap.departmentCapacity[dept].isOverCapacity = dayCap.departmentCapacity[dept].allocated > dayCap.departmentCapacity[dept].available;
      }

      // Update totals
      dayCap.allocatedManHours += chip.hours;
      dayCap.remainingManHours = dayCap.availableManHours - dayCap.allocatedManHours;
      dayCap.utilizationPercent = (dayCap.allocatedManHours / dayCap.availableManHours) * 100;
    }

    // Convert to sorted array
    return Array.from(capacityMap.values()).sort((a, b) => a.date.localeCompare(b.date));
  }

  /**
   * Check if any date has over-capacity
   */
  get hasOverCapacity(): boolean {
    return this.combinedCapacityByDate.some(cap =>
      cap.departmentCapacity && Object.values(cap.departmentCapacity).some(d => d.isOverCapacity)
    );
  }

  reset = () => {
    this.fileName = null;
    this.rawCsvData = "";
    this.pieces = [];
    this.moldGroupConfigs = [];
    this.validationErrors = [];
    this.projectId = "";
    this.bidNumber = "";
    this.uploading = false;
    this.saving = false;
    this.uploadProgress = 0;
    this.error = null;
    this.success = null;
    // Reset wizard and simulation state
    this.activeStep = 0;
    this.simulationData = null;
    this.simulationLoading = false;
    // Reset global phase durations to defaults
    this.setupHours = '2';
    this.castingHours = '1';  // 1 hour = 60 minutes
    this.finishingHours = '2,2';
    // Clear pending configs
    this.pendingMoldConfigs.clear();
    // Reset manual scheduling state
    this.schedulingMode = 'manual';
    this.manualPlacements.clear();
    this.draggedPieceIndex = null;
    this.existingCapacity = [];
  };
}

export default ProductionScheduleUploadViewStore;
